import i18n from '../../../i18n';
import type { ServiceTimelineReservation } from '../types';

/** Labels for classify_reservation_source ids (no PII). */
export const CHANNEL_LABEL: Record<string, string> = {
  get walkIn() { return i18n.t('chrome:channels.walkIn'); },
  get widget() { return i18n.t('chrome:channels.widget'); },
  get staff() { return i18n.t('chrome:channels.staff'); },
  get reserveWithGoogle() { return i18n.t('chrome:channels.reserveWithGoogle'); },
  get assistentAi() { return i18n.t('chrome:channels.assistentAi'); },
  get other() { return i18n.t('chrome:channels.other'); },
};

/** States that never mean a new live booking for snackbars. */
const TOAST_EXCLUDED_STATES = new Set([3, 6, 7, 14, 16]); // CANCELED, NOSHOW, STORNO, MOVED, CC_AUTO_DISMISSED

export type LiveBookingToast = {
  id: string;
  message: string;
};

function fmtTimeMs(ms: number): string {
  const d = new Date(ms);
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

export function channelLabel(channel: string): string {
  return CHANNEL_LABEL[channel] ?? channel;
}

export function formatBookingToast(r: ServiceTimelineReservation): string {
  const parts = [
    i18n.t('chrome:toasts.newBooking'),
    i18n.t('chrome:toasts.guest', { count: r.peopleCount }),
    fmtTimeMs(r.startMs),
    r.outletName,
    channelLabel(r.channel),
  ];
  return parts.filter(Boolean).join(' · ');
}

function isToastable(r: ServiceTimelineReservation): boolean {
  return !!r.id && r.peopleCount > 0 && !TOAST_EXCLUDED_STATES.has(r.state);
}

/**
 * Diff Live serviceTimeline reservations against previously seen ids.
 * First snapshot seeds the set (no toasts). Later polls toast new ids only.
 */
export function diffNewBookings(
  reservations: ServiceTimelineReservation[] | undefined | null,
  seenIds: Set<string>,
  seeded: boolean,
): {
  nextSeen: Set<string>;
  seeded: boolean;
  toasts: LiveBookingToast[];
} {
  const list = reservations ?? [];
  const nextSeen = new Set(seenIds);

  if (!seeded) {
    for (const r of list) {
      if (r.id) nextSeen.add(r.id);
    }
    return { nextSeen, seeded: true, toasts: [] };
  }

  const newcomers: ServiceTimelineReservation[] = [];
  for (const r of list) {
    if (!r.id || nextSeen.has(r.id)) continue;
    nextSeen.add(r.id);
    // Still mark cancelled/noshow ids seen so a later state flip doesn't toast.
    if (!isToastable(r)) continue;
    newcomers.push(r);
  }

  if (newcomers.length === 0) {
    return { nextSeen, seeded: true, toasts: [] };
  }

  // Newest first for the primary toast.
  newcomers.sort(
    (a, b) => (b.createdAtMs ?? b.startMs) - (a.createdAtMs ?? a.startMs),
  );

  if (newcomers.length === 1) {
    const r = newcomers[0]!;
    return {
      nextSeen,
      seeded: true,
      toasts: [{ id: r.id, message: formatBookingToast(r) }],
    };
  }

  const first = newcomers[0]!;
  return {
    nextSeen,
    seeded: true,
    toasts: [
      {
        id: `batch-${first.id}-${newcomers.length}`,
        message: i18n.t('chrome:toasts.batch', {
          count: newcomers.length,
          first: formatBookingToast(first),
        }),
      },
    ],
  };
}
